import { FinalizationSteps, isTerminalFinalizationStep } from "../../../constants/FinalizationSteps";
import type { TFinalizationState } from "./Finalization";

const NOTIFICATION_INTERVAL = 5 * 60 * 1000;
const NOTIFICATION_LIMIT = 6;
const NOTIFICATION_PREFIX = '[LONG SIGNUP]';

type TGetFinalizationState = () => TFinalizationState;
type TSendNotification = (notification: string) => void;

/** Notifies when the signup stops making progress. */
export default class LongSignupWatchdog {
    private readonly getFinalizationState: TGetFinalizationState;
    private readonly sendNotification: TSendNotification;
    private isActive = false;
    private notificationTimer: ReturnType<typeof setInterval> | null = null;
    private notificationCount = 0;
    private lastProgressAt = Date.now();
    private lastProgressFingerprint = '';

    constructor(getFinalizationState: TGetFinalizationState, sendNotification: TSendNotification) {
        this.getFinalizationState = getFinalizationState;
        this.sendNotification = sendNotification;
    }

    public readonly start = () => {
        if (this.isActive || isTerminalFinalizationStep(this.getFinalizationState().finalizationStep)) {
            return;
        }

        this.isActive = true;
        this.restartNotificationTimer();
    };

    private readonly stop = () => {
        this.isActive = false;
        this.clearNotificationTimer();
    };

    public readonly handleStateChange = () => {
        if (!this.isActive) {
            return;
        }

        if (isTerminalFinalizationStep(this.getFinalizationState().finalizationStep)) {
            this.stop();
            return;
        }

        if (this.getProgressFingerprint() === this.lastProgressFingerprint) {
            return;
        }

        this.restartNotificationTimer();
    };

    /** Hosting and local database creation report progress without changing the finalization step. */
    private readonly getProgressFingerprint = () => {
        const { finalizationStep, hostingInfo, localDatabaseCreationInfo } = this.getFinalizationState();

        return [finalizationStep, hostingInfo?.CurrentCreationStadiumNumber, localDatabaseCreationInfo?.progressStage, localDatabaseCreationInfo?.progressMessage].join('|');
    };

    private readonly restartNotificationTimer = () => {
        this.clearNotificationTimer();
        this.notificationCount = 0;
        this.lastProgressAt = Date.now();
        this.lastProgressFingerprint = this.getProgressFingerprint();
        this.notificationTimer = setInterval(this.notifyAboutMissingProgress, NOTIFICATION_INTERVAL);
    };

    private readonly clearNotificationTimer = () => {
        if (this.notificationTimer) {
            clearInterval(this.notificationTimer);
            this.notificationTimer = null;
        }
    };

    private readonly notifyAboutMissingProgress = () => {
        const { finalizationStep, progressMessage, hostingCreationInfo } = this.getFinalizationState();

        // Nothing to send the notification under yet, progress starts the watchdog over
        if (!hostingCreationInfo) {
            this.clearNotificationTimer();
            return;
        }

        this.notificationCount++;
        if (this.notificationCount >= NOTIFICATION_LIMIT) {
            this.clearNotificationTimer();
        }

        const minutesWithoutProgress = Math.round((Date.now() - this.lastProgressAt) / 60_000);
        const currentProgressMessage = typeof progressMessage === 'string' ? progressMessage : '';

        this.sendNotification(`${NOTIFICATION_PREFIX} No progress for ${minutesWithoutProgress} minutes. Step: ${FinalizationSteps[finalizationStep]}. Progress message: ${currentProgressMessage}`);
    };
}
